import { useState } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  AlertCircle,
  Check,
  User,
  Building2,
  Phone,
  Mail,
  Network,
  Users,
  CheckSquare,
} from 'lucide-react';
import { FlagImg } from '@/components/ui/CountryFlag';
import { phoneCodesSorted } from '@/data/phoneCodes';
// ─── Section types ──────────────────────────────────────────────────────────

type Section = 'regulatory' | 'mlro' | 'technical' | 'aml';

const LICENCE_PERMISSIONS = [
  { id: 'issuing_acquiring', label: 'Issuing / acquiring payment instruments' },
  { id: 'payment_execution', label: 'Executing payment transactions (credit transfers, direct debits)' },
  { id: 'money_remittance', label: 'Money remittance' },
  { id: 'aisp', label: 'Account information services (AISP)' },
  { id: 'pisp', label: 'Payment initiation services (PISP)' },
  { id: 'emoney', label: 'Issuing electronic money (e-money)' },
  { id: 'fx', label: 'Foreign exchange / currency exchange' },
  { id: 'payment_system', label: 'Operating a payment system' },
  { id: 'crypto', label: 'Crypto-asset services (CASP / VASP)' },
];

const SCA_METHODS = [
  { id: 'knowledge', label: 'Knowledge', description: 'Password, PIN, security question' },
  { id: 'possession', label: 'Possession', description: 'Mobile device, hardware token, OTP via SMS/email' },
  { id: 'inherence', label: 'Inherence', description: 'Biometrics: fingerprint, face ID, voice' },
];

const CLIENT_VOLUME_OPTIONS = [
  '1–100',
  '101–500',
  '501–2,000',
  '2,001–10,000',
  '10,000+',
];

const PAYMENT_VOLUME_OPTIONS = [
  'Under €50,000',
  '€50,000 – €250,000',
  '€250,000 – €1,000,000',
  '€1M – €5M',
  'Over €5M',
];

const AML_COMPONENTS = [
  { id: 'cdd', label: 'Customer Due Diligence (CDD) procedures' },
  { id: 'edd', label: 'Enhanced Due Diligence (EDD) for high-risk customers' },
  { id: 'tm', label: 'Transaction monitoring rules' },
  { id: 'sar', label: 'Suspicious Activity Reporting (SAR) process' },
  { id: 'records', label: 'Record keeping (minimum 5 years)' },
  { id: 'training', label: 'Staff AML training programme' },
  { id: 'sanctions', label: 'Sanctions & PEP screening' },
];

// ─── Progress dots ───────────────────────────────────────────────────────────

const SECTIONS: { key: Section; label: string }[] = [
  { key: 'regulatory', label: 'Regulatory' },
  { key: 'mlro', label: 'MLRO' },
  { key: 'technical', label: 'Technical' },
  { key: 'aml', label: 'AML Policy' },
];

function SectionDots({ current }: { current: Section }) {
  const idx = SECTIONS.findIndex(s => s.key === current);
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {SECTIONS.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full transition-all ${
            i < idx ? 'bg-primary' : i === idx ? 'bg-accent w-3 h-3' : 'bg-border'
          }`} />
          {i < SECTIONS.length - 1 && (
            <div className={`w-4 h-0.5 ${i < idx ? 'bg-primary' : 'bg-border'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Toggle component ─────────────────────────────────────────────────────

function YesNoToggle({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex gap-3">
      {[true, false].map(v => (
        <button
          key={String(v)}
          type="button"
          onClick={() => onChange(v)}
          className={`flex-1 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
            value === v
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40'
          }`}
        >
          {v ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────

export function CorporateBaaSStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const company = data.corporateCompany;
  const baas = (data as any).baasApplicationData || {};
  const directors = company?.directors || [];

  // Section A — Regulatory
  const [isRegulated, setIsRegulated] = useState<boolean | null>(
    baas.isRegulated !== undefined ? baas.isRegulated : null
  );
  const [regulatorName, setRegulatorName] = useState(baas.regulatorName || '');
  const [licenceNumber, setLicenceNumber] = useState(baas.licenceNumber || '');
  const [licencePermissions, setLicencePermissions] = useState<string[]>(baas.licencePermissions || []);
  const [otherPermission, setOtherPermission] = useState(baas.otherPermission || '');

  // Section B — MLRO
  const [mlroFullName, setMlroFullName] = useState(baas.mlroFullName || '');
  const [mlroEmail, setMlroEmail] = useState(baas.mlroEmail || '');
  const [mlroPhone, setMlroPhone] = useState(baas.mlroPhone || '');
  const [mlroPhoneCountryCode, setMlroPhoneCountryCode] = useState(baas.mlroPhoneCountryCode || '+44');
  const [showMlroPhonePicker, setShowMlroPhonePicker] = useState(false);
  const [mlroPhoneSearch, setMlroPhoneSearch] = useState('');
  const [mlroIsExistingPerson, setMlroIsExistingPerson] = useState<boolean | null>(
    baas.mlroIsExistingPerson !== undefined ? baas.mlroIsExistingPerson : null
  );
  const [mlroLinkedDirectorIndex, setMlroLinkedDirectorIndex] = useState<number | null>(
    baas.mlroLinkedDirectorIndex ?? null
  );

  // Section C — Technical
  const [clientFundsHandling, setClientFundsHandling] = useState<string>(baas.clientFundsHandling || '');
  const [scaMethods, setScaMethods] = useState<string[]>(baas.scaMethods || []);
  const [expectedClientsCount, setExpectedClientsCount] = useState(baas.expectedClientsCount || '');
  const [expectedMonthlyVolume, setExpectedMonthlyVolume] = useState(baas.expectedMonthlyVolume || '');

  // Section D — AML
  const [amlComponents, setAmlComponents] = useState<string[]>(baas.amlPolicyComponents || []);

  // Current section
  const [section, setSection] = useState<Section>('regulatory');
  const [showValidation, setShowValidation] = useState(false);

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
      </div>
    );
  }

  // ── Save helper ────────────────────────────────────────────────────────────
  const saveBaaSData = () => {
    updateData({
      baasApplicationData: {
        isRegulated,
        regulatorName,
        licenceNumber,
        licencePermissions,
        otherPermission,
        mlroFullName: mlroIsExistingPerson && mlroLinkedDirectorIndex !== null
          ? directors[mlroLinkedDirectorIndex]?.name || mlroFullName
          : mlroFullName,
        mlroEmail,
        mlroPhone,
        mlroPhoneCountryCode,
        mlroIsExistingPerson,
        mlroLinkedDirectorIndex,
        clientFundsHandling,
        scaMethods,
        expectedClientsCount,
        expectedMonthlyVolume,
        amlPolicyComponents: amlComponents,
      },
    } as any);
  };

  // ── Validation per section ─────────────────────────────────────────────────
  const isSectionAValid = () => {
    if (isRegulated === null) return false;
    if (isRegulated) {
      if (!regulatorName.trim() || !licenceNumber.trim()) return false;
      if (licencePermissions.length === 0) return false;
    }
    return true;
  };

  const isSectionBValid = () => {
    if (mlroIsExistingPerson === null) return false;
    if (mlroIsExistingPerson && mlroLinkedDirectorIndex === null) return false;
    if (!mlroIsExistingPerson) {
      if (!mlroFullName.trim()) return false;
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(mlroEmail)) return false;
      if (!mlroPhone.trim()) return false;
    }
    return true;
  };

  const isSectionCValid = () => {
    return (
      Boolean(clientFundsHandling) &&
      scaMethods.length > 0 &&
      Boolean(expectedClientsCount) &&
      Boolean(expectedMonthlyVolume)
    );
  };

  const isSectionDValid = () => amlComponents.length === AML_COMPONENTS.length;

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = () => {
    setShowValidation(true);
    if (section === 'regulatory' && !isSectionAValid()) return;
    if (section === 'mlro' && !isSectionBValid()) return;
    if (section === 'technical' && !isSectionCValid()) return;
    if (section === 'aml' && !isSectionDValid()) return;

    setShowValidation(false);

    if (section === 'regulatory') return setSection('mlro');
    if (section === 'mlro') return setSection('technical');
    if (section === 'technical') return setSection('aml');

    // Last section — save and continue
    saveBaaSData();
    goToNextStep();
  };

  const handleBack = () => {
    setShowValidation(false);
    if (section === 'mlro') return setSection('regulatory');
    if (section === 'technical') return setSection('mlro');
    if (section === 'aml') return setSection('technical');
  };

  const toggleItem = (arr: string[], item: string, setter: (v: string[]) => void) => {
    setter(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  // ── Render sections ────────────────────────────────────────────────────────

  const renderRegulatory = () => (
    <div className="space-y-5 animate-fade-in-up">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">Is your company regulated or licensed?</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          EMI, PI, crypto, or other financial services licence.
        </p>
        <YesNoToggle value={isRegulated} onChange={setIsRegulated} />
        {showValidation && isRegulated === null && (
          <p className="text-xs text-destructive mt-1">Please select an option</p>
        )}
      </div>

      {isRegulated && (
        <div className="space-y-4 animate-fade-in-up">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Regulator Name</label>
            <Input
              value={regulatorName}
              onChange={e => setRegulatorName(e.target.value)}
              placeholder="e.g. FCA, BaFin, CBI, CBM"
              className={showValidation && !regulatorName.trim() ? 'border-destructive' : ''}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Licence / Registration Number</label>
            <Input
              value={licenceNumber}
              onChange={e => setLicenceNumber(e.target.value)}
              placeholder="e.g. 123456"
              className={showValidation && !licenceNumber.trim() ? 'border-destructive' : ''}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">What permissions does your licence cover?</label>
            <p className="text-xs text-muted-foreground mb-3">Select all that apply</p>
            <div className="space-y-2">
              {LICENCE_PERMISSIONS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleItem(licencePermissions, p.id, setLicencePermissions)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                    licencePermissions.includes(p.id)
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                    licencePermissions.includes(p.id) ? 'bg-primary' : 'bg-muted border border-border'
                  }`}>
                    {licencePermissions.includes(p.id) && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <span className="text-sm">{p.label}</span>
                </button>
              ))}

              {/* Other */}
              <button
                type="button"
                onClick={() => toggleItem(licencePermissions, 'other', setLicencePermissions)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                  licencePermissions.includes('other')
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:border-muted-foreground/30'
                }`}
              >
                <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                  licencePermissions.includes('other') ? 'bg-primary' : 'bg-muted border border-border'
                }`}>
                  {licencePermissions.includes('other') && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                <span className="text-sm">Other</span>
              </button>

              {licencePermissions.includes('other') && (
                <Input
                  value={otherPermission}
                  onChange={e => setOtherPermission(e.target.value)}
                  placeholder="Please specify…"
                  className="mt-1"
                />
              )}
            </div>
            {showValidation && isRegulated && licencePermissions.length === 0 && (
              <p className="text-xs text-destructive mt-1">Please select at least one permission</p>
            )}
          </div>
        </div>
      )}

      {isRegulated === false && (
        <div className="animate-fade-in-up p-4 rounded-xl border border-border bg-muted/30">
          <p className="text-sm text-muted-foreground">
            No problem — we'll assess your application based on the products and services you offer.
          </p>
        </div>
      )}
    </div>
  );

  const renderMLRO = () => {
    const linkedDirector = mlroLinkedDirectorIndex !== null ? directors[mlroLinkedDirectorIndex] : null;
    return (
      <div className="space-y-5 animate-fade-in-up">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-accent" />
            <p className="font-semibold text-sm">Money Laundering Reporting Officer (MLRO)</p>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            The designated officer responsible for AML compliance in your organisation.
          </p>

          <p className="text-sm font-medium mb-3">Is your MLRO also a Director listed in this application?</p>
          <YesNoToggle value={mlroIsExistingPerson} onChange={v => {
            setMlroIsExistingPerson(v);
            setMlroLinkedDirectorIndex(null);
          }} />
          {showValidation && mlroIsExistingPerson === null && (
            <p className="text-xs text-destructive mt-1">Please select an option</p>
          )}
        </div>

        {mlroIsExistingPerson === true && (
          <div className="animate-fade-in-up space-y-2">
            <label className="text-sm font-medium block">Select the director who is the MLRO</label>
            {directors.length === 0 ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-xl">
                No directors found — please enter details manually below.
              </p>
            ) : (
              <div className="space-y-2">
                {directors.map((dir, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setMlroLinkedDirectorIndex(idx)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      mlroLinkedDirectorIndex === idx
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-card hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      mlroLinkedDirectorIndex === idx ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      {(dir.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{dir.name}</p>
                      <p className="text-xs text-muted-foreground">Director</p>
                    </div>
                    {mlroLinkedDirectorIndex === idx && (
                      <Check className="w-4 h-4 text-primary ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {showValidation && mlroIsExistingPerson && mlroLinkedDirectorIndex === null && (
              <p className="text-xs text-destructive">Please select a director</p>
            )}

            {linkedDirector && (
              <div className="mt-3 p-3 rounded-xl bg-primary/5 border border-primary/20 text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">{linkedDirector.name} — confirmed as MLRO</p>
                <p className="text-xs">MLRO contact details will be taken from their director record.</p>
              </div>
            )}
          </div>
        )}

        {(mlroIsExistingPerson === false || (mlroIsExistingPerson === true && directors.length === 0)) && (
          <div className="space-y-4 animate-fade-in-up">
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-muted-foreground" /> Full Name
              </label>
              <Input
                value={mlroFullName}
                onChange={e => setMlroFullName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className={showValidation && !mlroFullName.trim() ? 'border-destructive' : ''}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" /> Email Address
              </label>
              <Input
                type="email"
                value={mlroEmail}
                onChange={e => setMlroEmail(e.target.value)}
                placeholder="mlro@company.com"
                className={showValidation && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mlroEmail) ? 'border-destructive' : ''}
              />
            </div>
            <div className="relative">
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" /> Phone Number
              </label>
              <div className="flex gap-2">
                {/* Flag picker trigger */}
                <button
                  type="button"
                  onClick={() => { setShowMlroPhonePicker(v => !v); setMlroPhoneSearch(''); }}
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md border border-input bg-background hover:bg-muted/40 transition-colors text-sm whitespace-nowrap shrink-0"
                >
                  {(() => {
                    const sel = phoneCodesSorted.find(c => c.code === mlroPhoneCountryCode) || phoneCodesSorted[0];
                    return (
                      <>
                        <FlagImg code={sel.iso2} size="xs" />
                        <span className="font-medium">{sel.code}</span>
                        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${showMlroPhonePicker ? 'rotate-180' : ''}`} />
                      </>
                    );
                  })()}
                </button>
                <Input
                  type="tel"
                  value={mlroPhone}
                  onChange={e => setMlroPhone(e.target.value)}
                  placeholder="Phone number"
                  className={`flex-1 ${showValidation && !mlroPhone.trim() ? 'border-destructive' : ''}`}
                />
              </div>
              {/* Inline country search dropdown */}
              {showMlroPhonePicker && (
                <div className="absolute left-0 top-full mt-1 rounded-lg border border-border bg-popover overflow-hidden w-56 shadow-lg z-30">
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={mlroPhoneSearch}
                    autoFocus
                    onChange={e => setMlroPhoneSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-muted/40 border-b border-border focus:outline-none placeholder:text-muted-foreground"
                    style={{ fontSize: '16px' }}
                  />
                  <div className="max-h-44 overflow-y-auto">
                    {(() => {
                      const filtered = mlroPhoneSearch.trim()
                        ? phoneCodesSorted.filter(c =>
                            c.country.toLowerCase().includes(mlroPhoneSearch.toLowerCase()) ||
                            c.code.includes(mlroPhoneSearch)
                          )
                        : phoneCodesSorted;
                      return filtered.slice(0, 30).map((c, i) => (
                        <button
                          key={`${c.code}-${i}`}
                          type="button"
                          onClick={() => { setMlroPhoneCountryCode(c.code); setShowMlroPhonePicker(false); setMlroPhoneSearch(''); }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/60 transition-colors text-left text-xs border-b border-border/30 last:border-0"
                        >
                          <FlagImg code={c.iso2} size="xs" />
                          <span className="flex-1 truncate text-foreground">{c.country}</span>
                          <span className="text-muted-foreground font-medium shrink-0">{c.code}</span>
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTechnical = () => (
    <div className="space-y-6 animate-fade-in-up">
      {/* Client funds */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">Will your platform hold or pass through client funds?</p>
        </div>
        <div className="space-y-2 mt-3">
          {[
            { id: 'hold', label: 'Hold client funds in segregated accounts' },
            { id: 'passthrough', label: 'Pass-through only (no holding)' },
            { id: 'both', label: 'Both' },
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setClientFundsHandling(opt.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                clientFundsHandling === opt.id
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                clientFundsHandling === opt.id ? 'border-primary' : 'border-border'
              }`}>
                {clientFundsHandling === opt.id && <div className="w-2 h-2 rounded-full bg-primary" />}
              </div>
              <span className="text-sm">{opt.label}</span>
            </button>
          ))}
          {showValidation && !clientFundsHandling && (
            <p className="text-xs text-destructive">Please select an option</p>
          )}
        </div>
      </div>

      {/* SCA */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">How will your end-clients be authenticated? (SCA)</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Select all methods your platform uses</p>
        <div className="space-y-2">
          {SCA_METHODS.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => toggleItem(scaMethods, m.id, setScaMethods)}
              className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                scaMethods.includes(m.id)
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                scaMethods.includes(m.id) ? 'bg-primary' : 'bg-muted border border-border'
              }`}>
                {scaMethods.includes(m.id) && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              <div>
                <p className="text-sm font-medium">{m.label}</p>
                <p className="text-xs text-muted-foreground">{m.description}</p>
              </div>
            </button>
          ))}
          {showValidation && scaMethods.length === 0 && (
            <p className="text-xs text-destructive">Please select at least one SCA method</p>
          )}
        </div>
      </div>

      {/* Volume */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">Expected end-clients in the first 12 months?</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {CLIENT_VOLUME_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setExpectedClientsCount(opt)}
              className={`p-3 rounded-xl border-2 text-sm text-center transition-all ${
                expectedClientsCount === opt
                  ? 'border-primary bg-primary/10 font-semibold text-primary'
                  : 'border-border bg-card hover:border-muted-foreground/30 text-muted-foreground'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {showValidation && !expectedClientsCount && (
          <p className="text-xs text-destructive mt-1">Please select a range</p>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <Network className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">Expected monthly payment volume through our platform?</p>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Total payments processed on behalf of your clients</p>
        <div className="space-y-2">
          {PAYMENT_VOLUME_OPTIONS.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setExpectedMonthlyVolume(opt)}
              className={`w-full p-3 rounded-xl border-2 text-sm text-left transition-all ${
                expectedMonthlyVolume === opt
                  ? 'border-primary bg-primary/10 font-semibold text-primary'
                  : 'border-border bg-card hover:border-muted-foreground/30 text-muted-foreground'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {showValidation && !expectedMonthlyVolume && (
          <p className="text-xs text-destructive mt-1">Please select a range</p>
        )}
      </div>
    </div>
  );

  const renderAML = () => (
    <div className="space-y-5 animate-fade-in-up">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CheckSquare className="w-4 h-4 text-accent" />
          <p className="font-semibold text-sm">AML/CFT Policy Self-Certification</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Confirm that your AML/CFT policy includes all of the following components. All 7 must be confirmed to proceed.
        </p>
        <div className="space-y-2">
          {AML_COMPONENTS.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => toggleItem(amlComponents, c.id, setAmlComponents)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                amlComponents.includes(c.id)
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                amlComponents.includes(c.id) ? 'bg-primary' : 'bg-muted border border-border'
              }`}>
                {amlComponents.includes(c.id) && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              <span className="text-sm">{c.label}</span>
            </button>
          ))}
        </div>

        {/* Progress counter */}
        <div className={`mt-4 flex items-center justify-between p-3 rounded-xl border transition-all ${
          amlComponents.length === AML_COMPONENTS.length
            ? 'border-primary/30 bg-primary/5'
            : 'border-border bg-muted/20'
        }`}>
          <span className="text-sm text-muted-foreground">Confirmed components</span>
          <Badge variant={amlComponents.length === AML_COMPONENTS.length ? 'default' : 'secondary'}>
            {amlComponents.length} / {AML_COMPONENTS.length}
          </Badge>
        </div>

        {showValidation && amlComponents.length < AML_COMPONENTS.length && (
          <p className="text-xs text-destructive mt-2">
            All 7 components must be confirmed. Please review your AML/CFT policy before proceeding.
          </p>
        )}
      </div>
    </div>
  );

  const sectionIdx = SECTIONS.findIndex(s => s.key === section);
  const isFirstSection = sectionIdx === 0;

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Network className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-[0.1em] mb-1">
              BAAS APPLICATION
            </h1>
            <p className="text-muted-foreground text-sm">
              {SECTIONS[sectionIdx]?.label} — {sectionIdx + 1} of {SECTIONS.length}
            </p>
          </div>

          <SectionDots current={section} />

          {/* Section content */}
          {section === 'regulatory' && renderRegulatory()}
          {section === 'mlro' && renderMLRO()}
          {section === 'technical' && renderTechnical()}
          {section === 'aml' && renderAML()}
        </div>
      </div>

      {/* Fixed bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto flex gap-3">
          {!isFirstSection && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="h-14 px-5 rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <Button
            onClick={handleNext}
            className="flex-1 h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {section === 'aml' ? 'Complete & Continue' : 'Next'}
            {section !== 'aml' && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
